import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { ApiResponse, UserAPI } from '../../../../shared/types/api';
import { useExponentialBackoff } from '../../utils/exponentialBackoff';
import { getRewardfulClient } from '../tracking';

/**
 * Hook to fetch user's reservations
 */
export function useReservations() {
  return useQuery({
    queryKey: ['reservations'],
    queryFn: async (): Promise<UserAPI.GetReservationsResponse> => {
      return api.get('/user/reservations');
    },
  });
}

/**
 * Hook to create a new reservation
 */
export function useCreateReservation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UserAPI.CreateReservationRequest): Promise<UserAPI.CreateReservationResponse> => {
      return api.post('/user/reservations', data);
    },
    onSuccess: () => {
      // Invalidate reservations and events to refresh the lists
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

/**
 * Hook to get a single reservation by ID (for polling with exponential backoff)
 */
export function useReservation(
  id: string | null, 
  options?: { 
    enabled?: boolean; 
    refetchInterval?: number;
    useBackoff?: boolean;
  }
) {
  const backoff = useExponentialBackoff(500, 5000, 2);
  
  return useQuery({
    queryKey: ['reservation', id],
    queryFn: async (): Promise<UserAPI.GetReservationByIdResponse> => {
      return api.get(`/user/reservations/${id}`);
    },
    enabled: !!id && (options?.enabled !== false),
    refetchInterval: options?.useBackoff !== false 
      ? backoff.getInterval 
      : options?.refetchInterval,
  });
}

/**
 * Hook to update a reservation (e.g., cancel)
 */
export function useUpdateReservation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      id, 
      data 
    }: { 
      id: string; 
      data: UserAPI.UpdateReservationRequest  
    }): Promise<UserAPI.UpdateReservationResponse> => {
      return api.patch(`/user/reservations/${id}`, data);
    },
    onSuccess: (_, variables) => {
        // Invalidate reservations to refetch updated data
        queryClient.invalidateQueries({ queryKey: ['reservations'] });
        queryClient.invalidateQueries({ queryKey: ['reservation', variables.id] });
    },
  });
}

/**
 * Hook to confirm a reservation
 */
export function useConfirmReservation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<ApiResponse<void>> => {
      return await api.post<void>(`/user/reservations/${id}/confirm`);
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['reservation', id] });
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
    },
  });
}

/**
 * Hook to create payment for a booking
 */
export function useCreateBookingPayment() {
  return useMutation({
    mutationFn: async ({ 
      data
    }: { 
      data: UserAPI.CreateBookingPaymentRequest 
    }): Promise<UserAPI.CreateBookingPaymentResponse> => {
      const rewardful = getRewardfulClient();
      const referral = rewardful?.referral;
      return api.post(`/user/reservations/payment`, { ...data, referral });
    },
  });
}
